'use client';

import Script from 'next/script';

import { type CustomerMe } from '@vs-shop/rcprofi/effect/schema/openapi/backend';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    supportBoxChatId?: number;
    supportBoxChatSecret?: string;
    supportBoxChatVariables?: Record<string, string | number>;
    supportBoxApi?: {
      show: () => void;
      hide: () => void;
      open: () => void;
      close: () => void;
      isOnline: () => boolean;
      isShow: () => boolean;
    };
  }
}

type SupportBoxContext = {
  email?: string;
  fullName?: string;
  phone?: string;
  customerId?: number;
  orderedPrice?: string;
  orderedCount?: number;
  products?: string;
};

type SupportBoxChatScriptProps = {
  chatId: string;
  chatSecret: string;
  customer: CustomerMe | null;
};

export default function SupportBoxChatScript({
  chatId,
  chatSecret,
  customer,
}: SupportBoxChatScriptProps) {
  const ctx =
    customer === null
      ? {}
      : ({
          email: customer.email,
          fullName: customer.fullName ?? '',
          phone: customer.phoneNumber ?? '',
          customerId: customer.id ?? 0,
        } satisfies SupportBoxContext);

  // Both scripts share `strategy="afterInteractive"` so they execute in
  // declared order after hydration — the init block sets `window.supportBox*`
  // globals before the supportbox entry-point loads them.
  //
  // `beforeInteractive` is reserved for `app/layout.tsx` and `_document.tsx`
  // only; using it inside a deeper client component triggers React 19's
  // `Encountered a script tag while rendering React component` warning (the
  // inline `<script>` tag is rendered during SSR but never executed on the
  // client because hydration runs JSX, not raw `<script>`).
  return (
    <>
      <Script id="supportbox-init-variables" strategy="afterInteractive">
        {`
        window.supportBoxChatId = ${JSON.stringify(Number.parseInt(chatId, 10))};
        window.supportBoxChatSecret = ${JSON.stringify(chatSecret)};
        window.supportBoxChatVariables = ${JSON.stringify(ctx)};
        `}
      </Script>
      <Script
        src="https://chat.supportbox.cz/web-chat/entry-point"
        strategy="afterInteractive"
        async
        defer
      />
    </>
  );
}
